import React, { useEffect, useState, useCallback } from "react";
import { Users, MagnifyingGlass, DownloadSimple, ChartBar, PaperPlaneTilt, Sparkle, CheckCircle, Ticket, ChatCircleText, WhatsappLogo, EnvelopeSimple, DeviceMobile, CaretRight, ArrowLeft, CheckSquare, Square, BellRinging, X } from "@phosphor-icons/react";
import { PageLoader, EmptyState } from "../../components/shared";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import api, { API, TOKEN_KEY, money, apiErr } from "../../lib/api";

const CHANNELS = [{ v: "whatsapp", l: "WhatsApp", icon: WhatsappLogo }, { v: "sms", l: "SMS", icon: DeviceMobile }, { v: "email", l: "Email", icon: EnvelopeSimple }];
// which extra "change details" fields a template needs (auto-filled into the message)
const DETAIL_FIELDS = {
  event_rescheduled: ["new_date", "new_time", "new_venue"],
  datetime_change: ["new_date", "new_time"],
  venue_change: ["new_venue"],
  cancellation: ["refund_info"],
};
const FIELD_LABELS = { new_date: "New date", new_time: "New time", new_venue: "New venue", refund_info: "Refund / cancellation info" };

function Metric({ label, value, accent }) {
  return (
    <div className={`p-4 rounded-xl border ${accent ? "brand-gradient text-white border-transparent shadow-lg shadow-primary/20" : "bg-card border-border"}`}>
      <div className={`text-xs uppercase tracking-widest ${accent ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
      <div className="font-heading text-2xl font-semibold mt-1">{value ?? 0}</div>
    </div>
  );
}

export default function PublicEvents() {
  const [events, setEvents] = useState([]);
  const [eid, setEid] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/organizer/public-events").then((r) => setEvents(r.data.events)).finally(() => setLoading(false));
  }, []);

  if (loading) return <PageLoader />;
  if (eid) return <EventConsole eid={eid} title={(events.find((e) => e.id === eid) || {}).title} onBack={() => setEid(null)} />;

  return (
    <div>
      {events.length === 0 ? (
        <EmptyState icon={Ticket} title="No public events yet" subtitle="Publish a public ticketed event to manage its invitees and messaging here." testid="no-public-events" />
      ) : (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground mb-3">Select a public event to manage its invitees, messages and analytics.</p>
          {events.map((e) => (
            <button key={e.id} onClick={() => setEid(e.id)} data-testid={`public-event-${e.id}`}
              className="w-full flex items-center justify-between gap-3 p-4 rounded-xl border border-border bg-card hover:border-primary/40 transition-colors text-left">
              <div className="min-w-0">
                <div className="font-medium truncate">{e.title}</div>
                <div className="text-xs text-muted-foreground">{e.bookings} booking(s) · {e.status}</div>
              </div>
              <CaretRight size={18} className="text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function EventConsole({ eid, title, onBack }) {
  const [tab, setTab] = useState("attendees");
  const [selection, setSelection] = useState([]);
  const TABS = [["attendees", "Invitees", Users], ["messages", "Messages", ChatCircleText], ["analytics", "Analytics", ChartBar]];
  return (
    <div>
      <button onClick={onBack} className="text-sm text-muted-foreground inline-flex items-center gap-1 mb-3 hover:text-foreground" data-testid="pe-back"><ArrowLeft size={15} /> All public events</button>
      <h2 className="font-heading text-2xl font-semibold tracking-tight mb-4">{title}</h2>
      <div className="flex gap-2 mb-6 p-1 rounded-full bg-muted w-fit flex-wrap">
        {TABS.map(([v, l, Icon]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`pe-tab-${v}`}
            className={`inline-flex items-center gap-1.5 px-4 h-9 rounded-full text-sm font-semibold transition-colors ${tab === v ? "bg-foreground text-background" : "text-muted-foreground"}`}>
            <Icon size={15} /> {l}
          </button>
        ))}
      </div>
      {tab === "attendees" && <Attendees eid={eid} selection={selection} setSelection={setSelection} onNotify={(ids) => { setSelection(ids); setTab("messages"); }} />}
      {tab === "messages" && <Messages eid={eid} selection={selection} clearSelection={() => setSelection([])} />}
      {tab === "analytics" && <Analytics eid={eid} />}
    </div>
  );
}

function Attendees({ eid, selection, setSelection, onNotify }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState({ q: "", category: "all", payment: "all", checkin: "all", show: "all" });
  const [detail, setDetail] = useState(null);
  const [cmd, setCmd] = useState("");
  const [aiAns, setAiAns] = useState(null);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v && v !== "all") p.set(k, v); });
    return p.toString();
  }, [f]);

  const load = useCallback(() => { api.get(`/organizer/public-events/${eid}/attendees?${params()}`).then((r) => setData(r.data)); }, [eid, params]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelection(selection.includes(id) ? selection.filter((x) => x !== id) : [...selection, id]);
  const allIds = (data?.attendees || []).map((r) => r.ticket_id);
  const allSelected = allIds.length > 0 && allIds.every((id) => selection.includes(id));
  const toggleAll = () => setSelection(allSelected ? selection.filter((id) => !allIds.includes(id)) : Array.from(new Set([...selection, ...allIds])));

  const exportAs = async (fmt) => {
    try {
      const p = new URLSearchParams(params()); p.set("format", fmt);
      const res = await fetch(`${API}/organizer/public-events/${eid}/attendees/export?${p.toString()}`, { headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` } });
      if (!res.ok) throw new Error();
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement("a"); a.href = url; a.download = `invitees.${fmt}`; a.click(); URL.revokeObjectURL(url);
      toast.success(`Exported ${fmt.toUpperCase()}.`);
    } catch { toast.error("Could not export."); }
  };

  const runCmd = async () => {
    if (!cmd.trim()) return;
    setAiAns({ loading: true });
    try {
      const { data: res } = await api.post(`/organizer/public-events/${eid}/ai-command`, { query: cmd });
      setAiAns(res);
      if (res.filter) {
        setF((s) => ({ ...s, category: res.filter.category || "all", payment: res.filter.payment || "all",
          checkin: res.filter.checkin === "not_checked_in" ? "pending" : (res.filter.checkin || "all") }));
      }
    } catch (e) { toast.error(apiErr(e)); setAiAns(null); }
  };

  if (!data) return <PageLoader />;
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div>
      <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 mb-4">
        <div className="flex gap-2">
          <Input value={cmd} onChange={(e) => setCmd(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runCmd()}
            placeholder="Ask AI: 'Show VIP invitees who haven't checked in'…" className="h-10 bg-background" data-testid="pe-ai-input" />
          <Button onClick={runCmd} className="rounded-full h-10 px-4" data-testid="pe-ai-run"><Sparkle size={15} className="mr-1" weight="fill" /> Ask</Button>
        </div>
        {aiAns && !aiAns.loading && <p className="text-sm mt-2" data-testid="pe-ai-answer">{aiAns.answer}</p>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Metric label="Invitees" value={data.stats.total} accent />
        <Metric label="Checked in" value={data.stats.checked_in} />
        <Metric label="Paid" value={data.stats.paid} />
        <Metric label="Pending" value={data.stats.pending} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="relative flex-1 min-w-[160px]">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={f.q} onChange={(e) => set("q")(e.target.value)} placeholder="Search name / email / mobile / booking…" className="pl-9 h-10" data-testid="pe-search" />
        </div>
        <FSel value={f.category} on={set("category")} tid="pe-f-category" opts={[{ v: "all", l: "All categories" }, ...data.categories.map((c) => ({ v: c, l: c }))]} />
        <FSel value={f.payment} on={set("payment")} tid="pe-f-payment" opts={[{ v: "all", l: "Payment" }, { v: "paid", l: "Paid" }, { v: "pending", l: "Pending" }, { v: "refunded", l: "Refunded" }]} />
        <FSel value={f.checkin} on={set("checkin")} tid="pe-f-checkin" opts={[{ v: "all", l: "Check-in" }, { v: "checked_in", l: "Checked in" }, { v: "pending", l: "Not checked in" }]} />
        {data.shows.length > 1 && <FSel value={f.show} on={set("show")} tid="pe-f-show" opts={[{ v: "all", l: "All shows" }, ...data.shows.map((s) => ({ v: s, l: s }))]} />}
      </div>

      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="text-sm text-muted-foreground">{data.attendees.length} invitee(s){selection.length > 0 ? ` · ${selection.length} selected` : ""}</div>
        <div className="flex gap-2">
          {selection.length > 0 && (
            <Button size="sm" onClick={() => onNotify(selection)} className="rounded-full" data-testid="pe-notify-selected">
              <BellRinging size={14} className="mr-1" weight="fill" /> Notify {selection.length} selected
            </Button>
          )}
          {["csv", "xlsx", "pdf"].map((x) => <Button key={x} size="sm" variant="outline" onClick={() => exportAs(x)} className="rounded-full" data-testid={`pe-export-${x}`}><DownloadSimple size={14} className="mr-1" /> {x.toUpperCase()}</Button>)}
        </div>
      </div>

      {data.attendees.length === 0 ? (
        <EmptyState icon={Users} title="No invitees" subtitle="Ticket buyers for this public event will show here." testid="pe-no-attendees" />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-x-auto" data-testid="pe-attendees-table">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <tr>
                <th className="p-3 w-8"><button onClick={toggleAll} data-testid="pe-select-all">{allSelected ? <CheckSquare size={18} weight="fill" className="text-primary" /> : <Square size={18} />}</button></th>
                <th className="p-3 font-medium">Name</th><th className="p-3 font-medium">Contact</th><th className="p-3 font-medium">Ticket</th><th className="p-3 font-medium">Payment</th><th className="p-3 font-medium">Check-in</th><th className="p-3 font-medium">Show</th><th className="p-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.attendees.map((r) => (
                <tr key={r.ticket_id} className={`hover:bg-muted/40 ${selection.includes(r.ticket_id) ? "bg-primary/5" : ""}`} data-testid={`pe-attendee-${r.ticket_id}`}>
                  <td className="p-3"><button onClick={() => toggle(r.ticket_id)} data-testid={`pe-select-${r.ticket_id}`}>{selection.includes(r.ticket_id) ? <CheckSquare size={18} weight="fill" className="text-primary" /> : <Square size={18} className="text-muted-foreground" />}</button></td>
                  <td className="p-3 font-medium">{r.name}</td>
                  <td className="p-3 text-muted-foreground text-xs">{r.mobile || "—"}<br />{r.email || ""}</td>
                  <td className="p-3">{r.category} <span className="text-muted-foreground">×{r.quantity}</span><div className="text-[11px] text-muted-foreground">{r.ticket_number}</div></td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 rounded-full capitalize ${r.payment_status === "paid" ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : r.payment_status === "pending" ? "bg-amber-500/15 text-amber-300 border border-amber-500/25" : "bg-muted"}`}>{r.payment_status}</span></td>
                  <td className="p-3">{r.checked_in ? <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle size={14} weight="fill" /> In</span> : <span className="text-xs text-muted-foreground">Pending</span>}</td>
                  <td className="p-3 text-muted-foreground text-xs">{r.show}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button onClick={() => onNotify([r.ticket_id])} className="text-primary text-xs font-medium mr-3" data-testid={`pe-notify-${r.ticket_id}`}>Notify</button>
                    <button onClick={() => setDetail(r)} className="text-muted-foreground text-xs font-medium" data-testid={`pe-detail-${r.ticket_id}`}>Details</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="pe-detail-dialog">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-1.5">
              <Row k="Mobile" v={detail.mobile} /><Row k="Email" v={detail.email} />
              <Row k="Booking ID" v={detail.booking_id} /><Row k="Ticket #" v={detail.ticket_number} />
              <Row k="Category" v={`${detail.category} × ${detail.quantity}`} />
              <Row k="Inclusions" v={(detail.inclusions || []).join(", ") || "—"} />
              <Row k="Payment" v={detail.payment_status} /><Row k="Booking status" v={detail.booking_status} />
              <Row k="Checked in" v={detail.checked_in ? "Yes" : "No"} />
              <Row k="Show" v={detail.show} /><Row k="Seat / Zone" v={detail.seat || detail.zone || "—"} />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between gap-4"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v || "—"}</span></div>;
const FSel = ({ value, on, opts, tid }) => (
  <Select value={value} onValueChange={on}><SelectTrigger className="h-10 w-auto min-w-[120px]" data-testid={tid}><SelectValue /></SelectTrigger>
    <SelectContent>{opts.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent></Select>
);

function Messages({ eid, selection, clearSelection }) {
  const [templates, setTemplates] = useState(null);
  const [sel, setSel] = useState(null);
  const [body, setBody] = useState("");
  const [channel, setChannel] = useState("whatsapp");
  const [filters, setFilters] = useState({ category: "all", payment: "all", checkin: "all" });
  const [meta, setMeta] = useState({ categories: [] });
  const [est, setEst] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [aiInstr, setAiInstr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ov, setOv] = useState({ new_date: "", new_time: "", new_venue: "", refund_info: "" });

  const load = () => api.get(`/organizer/public-events/${eid}/msg-templates`).then((r) => { setTemplates(r.data.templates); if (!sel && r.data.templates[0]) { setSel(r.data.templates[0].type); setBody(r.data.templates[0].body); } });
  useEffect(() => { load(); api.get(`/organizer/public-events/${eid}/attendees`).then((r) => setMeta({ categories: r.data.categories })); }, [eid]); // eslint-disable-line

  const pick = (t) => { setSel(t.type); setBody(t.body); };
  const cur = (templates || []).find((t) => t.type === sel);
  const detailFields = DETAIL_FIELDS[sel] || [];
  const overrides = () => Object.fromEntries(Object.entries(ov).filter(([k, v]) => v && detailFields.includes(k)));
  const targeting = () => (selection && selection.length ? { ticket_ids: selection } : clean(filters));

  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/organizer/public-events/${eid}/msg-templates/${sel}`, { body }); toast.success(data.message); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const askAI = async (mode) => {
    setBusy(true);
    try { const { data } = await api.post(`/organizer/msg-templates/ai`, { mode, instruction: aiInstr, current: body, type: sel, event_id: eid, overrides: overrides() }); setBody(data.body); toast.success("AI drafted the message — review, then save."); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const estimate = async () => {
    try { const { data } = await api.post(`/organizer/public-events/${eid}/messages/estimate`, { channel, ...targeting() }); setEst(data); setConfirm(true); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doSend = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/organizer/public-events/${eid}/messages/send`, { channel, ...targeting(), template_type: sel, body_text: body, overrides: overrides() }); toast.success(data.message + (data.cost ? ` · ₹${data.cost} charged` : "")); setConfirm(false); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!templates) return <PageLoader />;

  return (
    <div className="grid lg:grid-cols-[220px_1fr] gap-5">
      <div className="space-y-1">
        <div className="overline mb-1">Templates</div>
        {templates.map((t) => (
          <button key={t.type} onClick={() => pick(t)} data-testid={`pe-tpl-${t.type}`}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${sel === t.type ? "bg-foreground text-background" : "hover:bg-muted"}`}>
            {t.label}{t.custom && <span className={`ml-1 text-[10px] ${sel === t.type ? "text-background/60" : "text-emerald-400"}`}>●</span>}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold">{cur?.label}</div>
            {cur?.moderation && <span className={`text-xs px-2 py-0.5 rounded-full ${cur.moderation.status === "approved" || cur.moderation.approved ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25" : "bg-red-500/15 text-red-300 border border-red-500/25"}`}>{(cur.moderation.status || (cur.moderation.approved ? "approved" : "rejected"))}</span>}
          </div>
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="text-sm" data-testid="pe-tpl-body" />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(cur?.variables || []).map((v) => <button key={v} onClick={() => setBody((b) => `${b}{${v}}`)} className="text-xs px-2 py-1 rounded bg-muted hover:bg-primary/10 hover:text-primary font-mono">{`{${v}}`}</button>)}
          </div>

          {detailFields.length > 0 && (
            <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="pe-change-details">
              <div className="text-xs font-semibold mb-2 text-primary">Change details (auto-filled into the message)</div>
              <div className="grid sm:grid-cols-3 gap-2">
                {detailFields.map((k) => (
                  k === "refund_info"
                    ? <Textarea key={k} value={ov[k]} onChange={(e) => setOv({ ...ov, [k]: e.target.value })} rows={2} placeholder={FIELD_LABELS[k]} className="sm:col-span-3 text-sm bg-background" data-testid={`pe-ov-${k}`} />
                    : <Input key={k} value={ov[k]} onChange={(e) => setOv({ ...ov, [k]: e.target.value })} placeholder={FIELD_LABELS[k]} className="h-9 bg-background" data-testid={`pe-ov-${k}`} />
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-1.5">Old date/time/venue fill automatically from each invitee's booking.</p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Input value={aiInstr} onChange={(e) => setAiInstr(e.target.value)} placeholder="Tell AI what to do (e.g. make it shorter)…" className="h-9 flex-1 min-w-[180px]" data-testid="pe-ai-instr" />
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => askAI("improve")} disabled={busy} data-testid="pe-ai-improve"><Sparkle size={13} className="mr-1" weight="fill" /> Improve</Button>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => askAI("rewrite")} disabled={busy} data-testid="pe-ai-rewrite">Rewrite</Button>
            <Button size="sm" onClick={save} disabled={busy} className="rounded-full" data-testid="pe-tpl-save">Save (AI review)</Button>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="font-semibold">Notify invitees</div>
          <div className="flex gap-2">
            {CHANNELS.map((c) => (
              <button key={c.v} onClick={() => setChannel(c.v)} data-testid={`pe-ch-${c.v}`}
                className={`inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-medium border transition-colors ${channel === c.v ? "border-primary bg-primary/5 text-primary" : "border-border"}`}>
                <c.icon size={15} /> {c.l}
              </button>
            ))}
          </div>

          {selection && selection.length > 0 ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm" data-testid="pe-selection-banner">
              <span className="inline-flex items-center gap-1.5 font-medium text-primary"><BellRinging size={15} weight="fill" /> Sending to {selection.length} selected invitee(s)</span>
              <button onClick={clearSelection} className="text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="pe-clear-selection"><X size={13} /> Send to all instead</button>
            </div>
          ) : (
            <div>
              <div className="text-xs text-muted-foreground mb-1.5">Target group</div>
              <div className="flex flex-wrap gap-2">
                <FSel value={filters.category} on={(v) => setFilters({ ...filters, category: v })} tid="pe-send-cat" opts={[{ v: "all", l: "All categories" }, ...meta.categories.map((c) => ({ v: c, l: c }))]} />
                <FSel value={filters.payment} on={(v) => setFilters({ ...filters, payment: v })} tid="pe-send-pay" opts={[{ v: "all", l: "All payments" }, { v: "paid", l: "Paid" }, { v: "pending", l: "Pending" }]} />
                <FSel value={filters.checkin} on={(v) => setFilters({ ...filters, checkin: v })} tid="pe-send-chk" opts={[{ v: "all", l: "All" }, { v: "checked_in", l: "Checked in" }, { v: "pending", l: "Not checked in" }]} />
              </div>
            </div>
          )}

          <Button onClick={estimate} className="rounded-full" data-testid="pe-send-estimate"><PaperPlaneTilt size={15} className="mr-1.5" /> Review & send</Button>
        </div>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="pe-send-dialog">
          <DialogHeader><DialogTitle>Confirm & send</DialogTitle></DialogHeader>
          {est && (
            <div className="text-sm space-y-2">
              <Row k="Template" v={cur?.label} />
              <Row k="Channel" v={channel} />
              <Row k="Recipients" v={est.recipients} />
              {est.skipped_no_contact > 0 && <Row k="Skipped (no contact)" v={est.skipped_no_contact} />}
              <Row k="Cost per message" v={est.unit_cost ? money(est.unit_cost) : "Free"} />
              <Row k="Estimated cost" v={money(est.estimated_cost)} />
              <Row k="Wallet balance" v={money(est.wallet_balance)} />
              {est.estimated_cost > est.wallet_balance && <p className="text-xs text-red-600">Insufficient wallet balance — recharge in Services Wallet.</p>}
            </div>
          )}
          <DialogFooter>
            <Button onClick={doSend} disabled={busy || (est && est.recipients === 0) || (est && est.estimated_cost > est.wallet_balance)} className="w-full rounded-full" data-testid="pe-send-confirm">
              Confirm & send to {est?.recipients || 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Analytics({ eid }) {
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/organizer/public-events/${eid}/analytics`).then((r) => setA(r.data)); }, [eid]);
  if (!a) return <PageLoader />;
  return (
    <div className="space-y-5" data-testid="pe-analytics">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Metric label="Bookings" value={a.total_bookings} accent />
        <Metric label="Tickets sold" value={a.tickets_sold} />
        <Metric label="Checked in" value={a.checked_in} />
        <Metric label="Revenue" value={money(a.revenue)} />
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold mb-3">Status</h3>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/25">Paid: {a.paid}</span>
            <span className="px-3 py-1.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/25">Pending: {a.pending}</span>
            <span className="px-3 py-1.5 rounded-full bg-red-500/15 text-red-300 border border-red-500/25">Refunded: {a.refunded}</span>
            <span className="px-3 py-1.5 rounded-full bg-muted">Not checked in: {a.not_checked_in}</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-heading font-semibold mb-3">Messages sent</h3>
          {Object.keys(a.messages || {}).length === 0 ? <p className="text-sm text-muted-foreground">No messages sent yet.</p> : (
            <div className="flex flex-wrap gap-2 text-sm">{Object.entries(a.messages).map(([ch, s]) => <span key={ch} className="px-3 py-1.5 rounded-full bg-muted capitalize">{ch}: {s.sent} sent{s.failed ? `, ${s.failed} failed` : ""}</span>)}</div>
          )}
        </div>
      </div>
      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="font-heading font-semibold mb-3">Ticket categories</h3>
        {a.by_category.length === 0 ? <p className="text-sm text-muted-foreground">No sales yet.</p> : (
          <div className="space-y-2">{a.by_category.map((c) => (
            <div key={c.name} className="flex justify-between text-sm"><span>{c.name} <span className="text-muted-foreground">× {c.qty}</span></span><span className="font-semibold">{money(c.revenue)}</span></div>
          ))}</div>
        )}
      </div>
    </div>
  );
}

// tiny helper to strip 'all' filter values before sending
function clean(obj) {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => { if (v && v !== "all") out[k] = v; });
  return out;
}
